import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Tabs, Tab } from "react-bootstrap";
import RlCloudSandboxGrid from "../RlCloudSandboxGrid";
import {
  BehavioralTabsData,
  MalwareConfigAnnotation
} from "../../../@types/rldynamicanalysis";
import { RlCloudSandboxBehavioralItems } from "./RlCloudSandboxBehavioralItems";
import {
  isMalwareConfigMutex,
  MALWARE_CONFIG_ANNOTATION,
  malwareConfigStyles,
  matchesConfigMutex,
  useSpectraCoreMalwareConfig,
  withMalwareConfigAnnotation
} from "../../malware_config";

type RlCloudSandboxBehavioralTabProps = {
  data: BehavioralTabsData | null;
};

type BehavioralTabsDataKey = keyof BehavioralTabsData;

const RlCloudSandboxBehavioralTabs = ({
  data
}: RlCloudSandboxBehavioralTabProps) => {
  const { hash } = useParams();
  const { mutexes: spectraCoreMutexes } = useSpectraCoreMalwareConfig(hash);

  const [activeTab, setActiveTab] = useState(
    RlCloudSandboxBehavioralItems.process_actions.title
  );

  const annotatedData = useMemo(
    () =>
      data
        ? {
            ...data,
            mutex_actions: (data.mutex_actions || []).map(action =>
              withMalwareConfigAnnotation(
                action,
                MALWARE_CONFIG_ANNOTATION.MUTEX,
                matchesConfigMutex(spectraCoreMutexes, action.name)
              )
            )
          }
        : data,
    [data, spectraCoreMutexes]
  );

  const handleTabsSelect = (tabsKey: string) => {
    setActiveTab(tabsKey);
  };

  const behavioralTabItems = Object.entries(RlCloudSandboxBehavioralItems).map(
    ([key, value]) => {
      const { title, schema } = value;
      return (
        <Tab
          key={title}
          eventKey={title}
          title={`${title} (${
            annotatedData?.[key as BehavioralTabsDataKey]?.length || 0
          })`}
          mountOnEnter
        >
          <RlCloudSandboxGrid
            data={annotatedData?.[key as BehavioralTabsDataKey]}
            schema={schema}
            key={key}
            shouldMarkRow={(item: {
              annotations?: MalwareConfigAnnotation[];
            }) => ({
              markRow: isMalwareConfigMutex(item),
              markRowClassName: malwareConfigStyles.annotated_row
            })}
          />
        </Tab>
      );
    }
  );

  return (
    <Tabs
      id="rl-cloud-sandbox-behavioral-tabs"
      variant="tabs"
      activeKey={activeTab}
      onSelect={handleTabsSelect}
      className="rlbs"
      transition={false}
    >
      {behavioralTabItems}
    </Tabs>
  );
};

export default RlCloudSandboxBehavioralTabs;
